import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { Podcast } from '@/types';

const STORAGE_KEY = 'cuebes_podcast_favorites';

interface FavoritesContextValue {
  favorites: Podcast[];
  isFavorite: (podcastId: number) => boolean;
  addFavorite: (podcast: Podcast) => void;
  removeFavorite: (podcastId: number) => void;
  toggleFavorite: (podcast: Podcast) => void;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

function loadFavorites(): Podcast[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load favorites:', error);
  }
  return [];
}

function saveFavorites(favorites: Podcast[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch (error) {
    console.error('Failed to save favorites:', error);
  }
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<Podcast[]>(() => loadFavorites());

  // Persist to localStorage when favorites change
  useEffect(() => {
    saveFavorites(favorites);
  }, [favorites]);

  const isFavorite = useCallback(
    (podcastId: number) => {
      return favorites.some((p) => p.id === podcastId);
    },
    [favorites]
  );

  const addFavorite = useCallback((podcast: Podcast) => {
    setFavorites((prev) => {
      if (prev.some((p) => p.id === podcast.id)) {
        return prev;
      }
      return [...prev, podcast];
    });
  }, []);

  const removeFavorite = useCallback((podcastId: number) => {
    setFavorites((prev) => prev.filter((p) => p.id !== podcastId));
  }, []);

  const toggleFavorite = useCallback(
    (podcast: Podcast) => {
      if (isFavorite(podcast.id)) {
        removeFavorite(podcast.id);
      } else {
        addFavorite(podcast);
      }
    },
    [isFavorite, addFavorite, removeFavorite]
  );

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        isFavorite,
        addFavorite,
        removeFavorite,
        toggleFavorite,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
